// CR075-P3: Batch Run Detail Page with Normalized and Deduped Views
// CR081: Removed Scan Config tab - S0 accordion now in Steps panel
// CR101: Added Rule Packs Used section
// CR109: Simplified tabs, consolidated summary with settings accordion
// CR111B: Added canary marker, ErrorBoundary wrapper, removed silent early returns
// CR158: Added Cancel Run dialog, Run Status Debug Header
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, RefreshCw, Play, Clock, CheckCircle2, XCircle, AlertCircle, Loader2, Eye, GitMerge, FileText, Trash2, Eraser, BookOpen, StopCircle, PauseCircle, Wrench, Copy, Check, Activity, Bug, MoreHorizontal, Settings2, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { BatchRunStepsPanel } from "@/components/admin/batch-runs/BatchRunStepsPanel";
import { StagingDealsView } from "@/components/admin/batch-runs/StagingDealsView";
import { NormalizedDealsView } from "@/components/admin/batch-runs/NormalizedDealsView";
import { DedupedDealsView } from "@/components/admin/batch-runs/DedupedDealsView";
import { DealCardTypesTable } from "@/components/admin/batch-runs/DealCardTypesTable";
import { DeleteBatchRunDialog } from "@/components/admin/batch-runs/DeleteBatchRunDialog";
import { ClearStagingDialog } from "@/components/admin/batch-runs/ClearStagingDialog";
import { CancelRunDialog } from "@/components/admin/batch-runs/CancelRunDialog";
import { BatchRunSummary, RulePackSource, DedupeMode } from "@/components/admin/batch-runs/BatchRunSummary";
import { StepsTabErrorBoundary } from "@/components/admin/batch-runs/StepsTabErrorBoundary";
import { SchedulerDebugHeader } from "@/components/admin/batch-runs/SchedulerDebugHeader";
import { RunStatusDebugHeader } from "@/components/admin/batch-runs/RunStatusDebugHeader";
import { KickoffStuckRunButton } from "@/components/admin/batch-runs/KickoffStuckRunButton";
import { StuckRunBanner } from "@/components/admin/batch-runs/StuckRunBanner";
import { BatchRunSettingsPanel } from "@/components/admin/batch-runs/BatchRunSettingsPanel";
import { deriveBatchRunDisplayStatus, type BatchRunStep as StuckRunStep } from "@/lib/batch-runs/stuckRunTypes";
import { PipelineTraceModal } from "@/components/admin/batch-runs/PipelineTraceModal";
import { ImageCaptureDebugModal } from "@/components/admin/batch-runs/ImageCaptureDebugModal";
import { computeEverStagedFromAttempts, EVER_STAGED_STEP_KEYS } from "@/lib/batch-runs/everStaged";

// CR111C: Canary marker for debugging blank screen issues
const CANARY_VERSION = "CR111C-v1";

interface BatchRun {
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  status: string;
  config_json: Record<string, unknown>;
  merchants_in_scope: number;
  sources_enabled: string[];
  total_staged_items: number;
  ever_staged_items?: number;
  ever_staged_updated_at?: string | null;
  normalized_count: number;
  deduped_count: number;
  published_count: number;
  rejected_count: number;
  error_count: number;
  // CR101: Rule pack fields
  normalize_rule_pack_id: string | null;
  normalize_rule_pack_source: RulePackSource | null;
  dedupe_rule_pack_id: string | null;
  dedupe_rule_pack_source: RulePackSource | null;
  // CR102: Dedup mode fields
  dedupe_mode: DedupeMode | null;
  dedupe_ai_enabled: boolean | null;
  dedupe_ai_provider: string | null;
  dedupe_ai_confidence_threshold: number | null;
}

interface BatchRunStep {
  id: string;
  batch_run_id: string;
  step_key: string;
  step_name: string;
  status: string;
  mode: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  records_in: number;
  records_out: number;
  skipped_reason: string | null;
  error_message: string | null;
  config_json: Record<string, unknown>;
  updated_at?: string; // CR162: Added for stuck detection
}

// CR166: Extended status config to include NEEDS_REVIEW
const statusConfig: Record<string, { label: string; icon: React.ReactNode; variant: "default" | "secondary" | "destructive" | "outline" | "warning" }> = {
  CREATED: { label: "Created", icon: <Clock className="h-3 w-3" />, variant: "secondary" },
  RUNNING: { label: "Running", icon: <Loader2 className="h-3 w-3 animate-spin" />, variant: "default" },
  NEEDS_REVIEW: { label: "Needs Review", icon: <PauseCircle className="h-3 w-3" />, variant: "warning" },
  COMPLETE: { label: "Complete", icon: <CheckCircle2 className="h-3 w-3" />, variant: "outline" },
  FAILED: { label: "Failed", icon: <XCircle className="h-3 w-3" />, variant: "destructive" },
  CANCELLED: { label: "Cancelled", icon: <AlertCircle className="h-3 w-3" />, variant: "secondary" },
  PARTIAL: { label: "Partial", icon: <AlertCircle className="h-3 w-3" />, variant: "secondary" },
};

export default function BatchRunDetail() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [run, setRun] = useState<BatchRun | null>(null);
  const [steps, setSteps] = useState<BatchRunStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("steps");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [showCaptureDebugModal, setShowCaptureDebugModal] = useState(false);
  const [showBatchRunSettingsDialog, setShowBatchRunSettingsDialog] = useState(false);
  const [everStagedFallback, setEverStagedFallback] = useState<number>(0);

  /* CR: Add ref to prevent overlapping fetches */
  const isFetchingRef = useRef(false);

  const fetchRunDetails = useCallback(async (isBackground = false) => {
    if (!runId) return;

    // Prevent overlapping fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (isBackground) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [runResult, stepsResult, attemptsResult] = await Promise.all([
        supabase
          .from("cr075_batch_runs")
          .select("*")
          .eq("id", runId)
          .single(),
        supabase
          .from("cr075_batch_run_steps")
          .select("*")
          .eq("batch_run_id", runId)
          .order("step_key", { ascending: true }),
        supabase
          .from("cr075_batch_run_step_attempts")
          .select("step_key, records_out, merchant_source_id, status")
          .eq("batch_run_id", runId)
          .in("step_key", [...EVER_STAGED_STEP_KEYS])
          .is("merchant_source_id", null),
      ]);

      if (runResult.error) throw runResult.error;
      setRun(runResult.data as BatchRun);
      setSteps((stepsResult.data || []) as BatchRunStep[]);
      if (attemptsResult.error) {
        console.error("Failed to fetch batch run attempts for ever-staged fallback:", attemptsResult.error);
        setEverStagedFallback(0);
      } else {
        setEverStagedFallback(
          computeEverStagedFromAttempts(
            (attemptsResult.data || []).map((attempt) => ({
              step_key: attempt.step_key,
              records_out: attempt.records_out,
              merchant_source_id: attempt.merchant_source_id,
              status: attempt.status,
            })),
          ),
        );
      }
    } catch (err) {
      console.error("Failed to fetch batch run details:", err);
      toast({
        title: "Error",
        description: "Failed to load batch run details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
      // Add a small delay before allowing next fetch to debounce rapid triggers
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 500);
    }
  }, [runId, toast]);

  // Stable no-arg callback for child components — always background refresh to avoid flicker
  const refreshInBackground = useCallback(() => fetchRunDetails(true), [fetchRunDetails]);

  useEffect(() => {
    fetchRunDetails(false);
  }, [fetchRunDetails]);

  // Keep steps/status in sync without manual refresh.
  // This covers missed child callbacks between step transitions (e.g. S1.2→S1.4, S6→S7A).
  useEffect(() => {
    if (!runId || activeTab !== "steps") return;

    const activeStatuses = new Set(["CREATED", "RUNNING", "PENDING", "ENQUEUED"]);
    const runIsActive = !!run?.status && activeStatuses.has(run.status);
    const hasActiveStep = steps.some((s) => activeStatuses.has(s.status));

    // Continue polling briefly after active work clears so dependent step gating can settle.
    const now = Date.now();
    const recentTransition = steps.some((s) => {
      if (!s.updated_at) return false;
      const ts = Date.parse(s.updated_at);
      return Number.isFinite(ts) && now - ts < 45_000;
    });

    if (!runIsActive && !hasActiveStep && !recentTransition) return;

    const interval = setInterval(() => {
      fetchRunDetails(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [runId, activeTab, run?.status, steps, fetchRunDetails]);

  // CR111B: Loading state - visible skeleton, never blank
  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          {/* CR111B: Canary marker to verify component mounted */}
          <div className="text-xs text-muted-foreground/50 font-mono">
            [Canary: {CANARY_VERSION}] BatchRunDetail loading...
          </div>
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AdminLayout>
    );
  }

  // CR111B: Not found state - visible message, never blank
  if (!run) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          {/* CR111B: Canary marker to verify component mounted */}
          <div className="text-xs text-muted-foreground/50 font-mono">
            [Canary: {CANARY_VERSION}] BatchRunDetail: run not found
          </div>
          <div className="text-center py-12">
            <p className="text-muted-foreground">Batch run not found.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/batch-runs")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Batch Runs
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // CR166: Derive display status from steps and run state
  const stepsForDerived: StuckRunStep[] = steps.map((s) => ({
    id: s.id,
    step_key: s.step_key,
    step_name: s.step_name,
    status: s.status,
    mode: s.mode,
    updated_at: s.updated_at,
    started_at: s.started_at,
  }));
  const derivedStatus = deriveBatchRunDisplayStatus(run.status, stepsForDerived, run.error_count);
  const statusInfo = statusConfig[derivedStatus.status] || statusConfig.CREATED;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header Row 1: Back + Title + Status + Primary Actions + More dropdown */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/admin/batch-runs")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CopyUuidTitle uuid={run.id} />
            <Badge variant={statusInfo.variant} className="gap-1" title={derivedStatus.helperText}>
              {statusInfo.icon}
              {statusInfo.label}
            </Badge>
            {derivedStatus.status === "NEEDS_REVIEW" && (
              <span className="text-xs text-warning">Awaiting review</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => fetchRunDetails(true)} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <KickoffStuckRunButton
              batchRunId={run.id}
              runStatus={run.status}
              sourcesEnabled={run.sources_enabled}
              steps={steps.map((s) => ({ id: s.id, step_key: s.step_key, step_name: s.step_name, status: s.status, mode: s.mode }))}
              onKickedOff={refreshInBackground}
            />
            {run.status === "RUNNING" && (
              <Button variant="outline" size="sm" onClick={() => setShowCancelDialog(true)} className="text-destructive hover:text-destructive">
                <StopCircle className="h-4 w-4 mr-1.5" />
                Cancel
              </Button>
            )}
            {/* More dropdown: tools + destructive actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-2">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs">Settings</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setShowBatchRunSettingsDialog(true)}>
                  <Settings2 className="h-4 w-4 mr-2" />
                  Batch Runs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Tools</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setShowTraceModal(true)}>
                  <Activity className="h-4 w-4 mr-2" />
                  Pipeline Trace
                </DropdownMenuItem>
                {(run.config_json as Record<string, unknown>)?.capture_mode === "IMAGE_CAPTURE" && (
                  <DropdownMenuItem onClick={() => setShowCaptureDebugModal(true)}>
                    <Bug className="h-4 w-4 mr-2" />
                    Capture Debug
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => window.open('https://github.com/user/repo/blob/main/docs/CR075_BATCH_RUN_RUNBOOK.md', '_blank')}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Runbook
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setShowClearDialog(true)} className="text-destructive focus:text-destructive">
                  <Eraser className="h-4 w-4 mr-2" />
                  Clear Staging
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Run
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Header Row 2: Timestamp */}
        <div className="-mt-4">
          <p className="text-xs text-muted-foreground">
            Created {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
            {run.created_by && ` by ${run.created_by}`}
          </p>
        </div>

        {/* CR157: Scheduler Debug Header (only shows for scheduler-created runs) */}
        <SchedulerDebugHeader
          configJson={run.config_json}
          sourcesEnabled={run.sources_enabled}
        />

        {/* CR158: Run Status Debug Header (only shows when RUNNING) */}
        <RunStatusDebugHeader
          steps={steps as Array<{ id: string; step_key: string; step_name: string; status: string; updated_at?: string; }>}
          runStatus={run.status}
        />

        {/* CR162: Stuck Run Banner (shows when RUNNING but no active steps) */}
        <StuckRunBanner
          batchRunId={run.id}
          runStatus={run.status}
          steps={steps.map((s) => ({
            id: s.id,
            step_key: s.step_key,
            step_name: s.step_name,
            status: s.status,
            mode: s.mode,
            updated_at: s.updated_at,
            started_at: s.started_at,
          }))}
          configJson={run.config_json}
          sourcesEnabled={run.sources_enabled}
          onRecoveryAction={refreshInBackground}
        />

        {/* CR414: Repair Steps banner — shown when run exists but has no steps */}
        {!loading && run && steps.length === 0 && (
          <RepairStepsBanner batchRunId={run.id} onRepaired={refreshInBackground} />
        )}

        {/* CR109: Consolidated Overall Summary (Stats + Settings Accordion) */}
        <BatchRunSummary run={{ ...run, ever_staged_fallback: everStagedFallback }} />

        {/* CR109/CR553: Tabs with clean names — promoted styling + Admin/Live tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-11 p-1">
            <TabsTrigger value="steps" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <Play className="h-4.5 w-4.5" />
              Steps
            </TabsTrigger>
            <TabsTrigger value="staging" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <Eye className="h-4.5 w-4.5" />
              Staging
            </TabsTrigger>
            <TabsTrigger value="normalized" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <FileText className="h-4.5 w-4.5" />
              Normalize
            </TabsTrigger>
            <TabsTrigger value="deduped" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <GitMerge className="h-4.5 w-4.5" />
              Dedup
            </TabsTrigger>
            <TabsTrigger value="admin" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <Settings2 className="h-4.5 w-4.5" />
              Admin
            </TabsTrigger>
            <TabsTrigger value="live" className="gap-2 text-sm font-semibold px-4 py-2 data-[state=active]:shadow-md">
              <Globe className="h-4.5 w-4.5" />
              Live
            </TabsTrigger>
          </TabsList>

          <TabsContent value="steps" className="mt-4">
            {/* CR111B: Wrap entire Steps tab content in ErrorBoundary */}
            <StepsTabErrorBoundary onRetry={refreshInBackground}>
              <BatchRunStepsPanel
                batchRunId={run.id}
                steps={steps}
                runStatus={run.status}
                sourcesEnabled={run.sources_enabled}
                currentConfig={run.config_json}
                onStepExecuted={refreshInBackground}
                isSchedulerRun={run.config_json?.from_schedule === true}
                onViewOutput={(stepKey) => {
                  if (stepKey === "S5_CROP" || stepKey === "S1_5" || stepKey === "S1_SNIPPET" || stepKey === "S1_4") {
                    // CR485/CR502: Navigate to Snippet Artifacts view
                    window.open(`/admin/flyer-snippets?batch_run_id=${run.id}`, "_blank");
                  } else if (stepKey === "S4") {
                    setActiveTab("normalized");
                  } else if (stepKey === "S5") {
                    setActiveTab("deduped");
                  } else {
                    setActiveTab("staging");
                  }
                }}
              />
            </StepsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="staging" className="mt-4">
            <StagingDealsView batchRunId={run.id} sourcesEnabled={run.sources_enabled} />
          </TabsContent>

          <TabsContent value="normalized" className="mt-4">
            <NormalizedDealsView batchRunId={run.id} />
          </TabsContent>

          <TabsContent value="deduped" className="mt-4">
            <DedupedDealsView batchRunId={run.id} onPromoteComplete={refreshInBackground} />
          </TabsContent>

          {/* CR553: Admin tab — all deal card types */}
          <TabsContent value="admin" className="mt-4">
            <DealCardTypesTable mode="admin" />
          </TabsContent>

          {/* CR553: Live tab — live-visible deal card types */}
          <TabsContent value="live" className="mt-4">
            <DealCardTypesTable mode="live" />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Dialog - CR158: Pass steps for stale detection */}
      <DeleteBatchRunDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        batchRunId={run.id}
        status={run.status}
        publishedCount={run.published_count}
        onDeleted={() => navigate("/admin/batch-runs")}
        steps={steps as Array<{ id: string; step_key: string; step_name: string; status: string; updated_at?: string; }>}
        onShowCancelDialog={() => setShowCancelDialog(true)}
      />

      {/* Clear Staging Dialog */}
      <ClearStagingDialog
        open={showClearDialog}
        onOpenChange={setShowClearDialog}
        batchRunId={run.id}
        onCleared={refreshInBackground}
      />

      {/* CR158: Cancel Run Dialog */}
      <CancelRunDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        batchRunId={run.id}
        runStatus={run.status}
        steps={steps as Array<{ id: string; step_key: string; step_name: string; status: string; updated_at?: string; started_at?: string | null; }>}
        onCancelled={refreshInBackground}
      />

      {/* CR460: Pipeline Trace & Diagnostics Modal */}
      <PipelineTraceModal
        open={showTraceModal}
        onOpenChange={setShowTraceModal}
        batchRunId={run.id}
      />

      {/* CR462: Image Capture Debug Modal */}
      <ImageCaptureDebugModal
        open={showCaptureDebugModal}
        onOpenChange={setShowCaptureDebugModal}
        batchRunId={run.id}
      />

      <Dialog open={showBatchRunSettingsDialog} onOpenChange={setShowBatchRunSettingsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
          </DialogHeader>
          <BatchRunSettingsPanel run={run} />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

// CR414/CR415: Copy UUID title component — clickable title + clipboard fallback
function CopyUuidTitle({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(uuid);
      } else {
        // CR415: Fallback using hidden textarea for older browsers / insecure contexts
        const textarea = document.createElement('textarea');
        textarea.value = uuid;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      toast({ title: "Copied", description: `Full UUID copied: ${uuid}` });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Could not copy to clipboard", variant: "destructive" });
    }
  };

  return (
    <div className="flex items-center gap-1.5 group">
      <h1
        className="text-2xl font-bold tracking-tight cursor-pointer hover:text-primary transition-colors select-none"
        onClick={handleCopy}
        title={`Click to copy: ${uuid}`}
      >
        Batch Run {uuid.slice(0, 8)}
      </h1>
      <button
        onClick={handleCopy}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
        title={`Click to copy: ${uuid}`}
      >
        {copied ? (
          <Check className="h-4 w-4 text-primary" />
        ) : (
          <Copy className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
    </div>
  );
}

// CR414: Inline component for repairing runs with missing steps
function RepairStepsBanner({ batchRunId, onRepaired }: { batchRunId: string; onRepaired: () => void }) {
  const [repairing, setRepairing] = useState(false);
  const { toast } = useToast();

  const handleRepair = async () => {
    setRepairing(true);
    try {
      const { data, error } = await supabase.rpc("rpc_cr075_repair_batch_run_steps", {
        p_batch_run_id: batchRunId,
      });
      if (error) throw error;
      toast({
        title: "Steps Repaired",
        description: `Seeded ${data} missing step(s)`,
      });
      onRepaired();
    } catch (err: unknown) {
      const pgError = err as { code?: string; message?: string };
      toast({
        title: "Repair Failed",
        description: pgError.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRepairing(false);
    }
  };

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Missing Steps</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>This batch run has no pipeline steps. It cannot execute until steps are seeded.</span>
        <Button size="sm" variant="outline" onClick={handleRepair} disabled={repairing}>
          {repairing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
          Repair Steps
        </Button>
      </AlertDescription>
    </Alert>
  );
}
